import DisplayPropertiesCard from "@covertree/ui/content/DisplayPropertiesCard";
import type { AdditionalInterest } from "@covertree/new-business-graphql";
import { mapAdditionalInterestTypeToLabel } from "@covertree/new-business-mappings";
import { Box, Chip, TooltipProps } from "@mui/material";
import { MenuItemDef } from "@covertree/types";
import { MeatballMenu } from "@covertree/ui";
import { formatAdditionalInterestAddress } from "./utils";

type AdditionalInterestCardProps = {
  additionalInterest: Omit<AdditionalInterest, "additionalInterestId">;
  isCheckout?: boolean;
  isEndorsementCheckout?: boolean;
  actionMenuItems?: MenuItemDef[];
  disabled?: boolean;
  tooltip?: string;
  tooltipPlacement?: TooltipProps["placement"];
  fontSize?: number;
  labelFontSize?: number;
};

function AdditionalInterestCard({
  additionalInterest,
  isCheckout = false,
  isEndorsementCheckout = false,
  actionMenuItems = [],
  disabled = false,
  tooltip = "",
  tooltipPlacement = "top",
  fontSize = 18,
  labelFontSize = 16,
}: AdditionalInterestCardProps) {
  const checkout = isCheckout || isEndorsementCheckout;
  const showBillingChip =
    additionalInterest.isBilling && (!checkout || isEndorsementCheckout);
  const showTopRightMenu = !checkout && actionMenuItems.length !== 0;
  const hasTopRightContent = showBillingChip || showTopRightMenu;

  return (
    <Box
      position="relative"
      sx={{
        ".MuiTypography-subtitle2": { fontSize: labelFontSize },
        ".MuiTypography-body1": { fontSize },
      }}>
      <DisplayPropertiesCard
        properties={[
          {
            label: "Name",
            value: additionalInterest.name,
            showAsTypography: true,
            gridSize: { xs: 12, lg: checkout ? 4 : 5 },
          },
          {
            label: "Type",
            value: mapAdditionalInterestTypeToLabel(additionalInterest.type),
            showAsTypography: true,
            gridSize: { xs: 12, lg: checkout ? 8 : 5 },
          },
          {
            label: "Address",
            value: formatAdditionalInterestAddress(additionalInterest),
            showAsTypography: true,
            gridSize: 12,
          },
          {
            label: "Email",
            value: additionalInterest.email || "N/A",
            showAsTypography: true,
            gridSize: 12,
            isHidden: checkout,
          },
          {
            label: "Loan / account #",
            value: additionalInterest.accountNumber || "N/A",
            showAsTypography: true,
            gridSize: 12,
          },
        ]}>
        {checkout && actionMenuItems.length !== 0 && (
          <MeatballMenu
            actionMenuItems={actionMenuItems}
            border
            closeOnClick
            wrapperProps={{ width: "38px" }}
            disabled={disabled}
          />
        )}
      </DisplayPropertiesCard>

      {hasTopRightContent && (
        <Box
          position="absolute"
          top={16}
          right={16}
          display="flex"
          alignItems="center"
          gap={1}>
          {showBillingChip && (
            <Chip
              color="secondary"
              label="Billing"
              size="medium"
              sx={{ px: "18px", fontSize: 16 }}
            />
          )}
          {showTopRightMenu && (
            <MeatballMenu
              actionMenuItems={actionMenuItems}
              border
              closeOnClick
              wrapperProps={{ width: "38px" }}
              disabled={disabled}
              tooltip={tooltip}
              tooltipPlacement={tooltipPlacement}
            />
          )}
        </Box>
      )}
    </Box>
  );
}

export { AdditionalInterestCard };
